import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, Observable, switchMap } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { TagColorInfo, TagColorPalette } from '@blaze-runtime/engine-domain';
import {
  DataBindingSourceInfo,
  PropertiesConfigInputColorPaletteModel,
  PropertiesConfigInputListColumnModel,
  PropertiesConfigInputTableColumnModel,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { ColumnQueryLike, findColumn, getDataValue } from '@blaze-shared/schema-domain';
import {
  BaseSchema,
  BlazeSchema,
  DatasetSchema,
  type ElementSchema,
} from '@blaze-shared/schema-domain';
import { getCssMixedColor, isNullish } from '@blaze-shared/shared-util';
import {
  AppExtendedThemeValue,
  COLOR_PALETTE_LENGTH,
  IThemeService,
  makeThemePaletteValueCss,
} from '@blaze-shared/theming-domain';

export const TAGS_FOREGROUND_THEME_KEY = 'value';
export const TAGS_BACKGROUND_THEME_KEY = 'accent';

@Injectable({ providedIn: 'root' })
export class ElementTagsService {
  private readonly store = inject(Store);
  private readonly themeService = inject(IThemeService);
  private readonly schemaService = inject(SchemaService);

  createSchemaObservable(
    dataSource$: Observable<DataBindingSourceInfo | undefined>,
  ): Observable<BlazeSchema | ElementSchema | undefined> {
    return dataSource$.pipe(
      filter((datasource): datasource is DataBindingSourceInfo => !!datasource),
      switchMap((dataSource) => {
        if (this.schemaService.isDataset(dataSource.id)) {
          return this.store.select(runtimeSelectors.selectSchemaById(dataSource.id));
        }
        return this.store.select(runtimeSelectors.selectWorkflowsSchemaById(dataSource.id));
      }),
    );
  }

  mapValuesToColorPalette(
    values?: any[],
    columnIdOrName?: string,
    schema?: BaseSchema | DatasetSchema,
    tagsPalette?: PropertiesConfigInputColorPaletteModel,
    overrideForeground?: string,
    triplePalette?: boolean,
  ): TagColorPalette | undefined {
    if (tagsPalette?.paletteName === 'schema' && columnIdOrName) {
      return this.createColorsFromSchema(columnIdOrName, schema);
    }

    if (tagsPalette?.paletteName === 'single') {
      return this.createColorsFromSinglePalette(tagsPalette);
    }

    return this.createColorsFromPalette(tagsPalette, values, overrideForeground, triplePalette);
  }

  mapColumnValuesToColorPalette(
    column: PropertiesConfigInputTableColumnModel | PropertiesConfigInputListColumnModel,
    schema?: BaseSchema | DatasetSchema,
    values?: string[],
  ): TagColorPalette | undefined {
    const { tagsPalette } = column;
    switch (tagsPalette?.paletteName) {
      case undefined:
        return undefined;
      case 'schema':
        return this.createColorsFromSchema(column, schema);
      case 'single':
        return this.createColorsFromSinglePalette(tagsPalette);
      default:
        return this.createColorsFromPalette(tagsPalette, values);
    }
  }

  /**
   * Checking background if it is from theme is used to determine if opacity should be applied to tag background.
   * Values from table column model are not processed by theme. They are checked if it is a theme reference.
   * Values from tags element are parsed by theme so value comparison is used.
   */
  normalizeSinglePaletteBgColor(currentColor: string, themeValue?: AppExtendedThemeValue): string {
    const isValueTakenFromTheme =
      this.themeService.isThemeRef(currentColor) ||
      themeValue?.[TAGS_BACKGROUND_THEME_KEY] === currentColor;

    return isValueTakenFromTheme ? `${currentColor.substring(0, 7)}0A` : currentColor;
  }

  private createColorsFromSchema(
    columnQueryLike: ColumnQueryLike,
    schema?: BaseSchema | DatasetSchema,
  ): TagColorPalette | undefined {
    const paletteFromSchema = findColumn(schema?.columns, columnQueryLike)?.tagDefinition?.items;
    if (!paletteFromSchema) {
      return;
    }

    const colors: Record<string, TagColorInfo> = {};
    paletteFromSchema.forEach((p) => {
      colors[p.value] = { foreground: p.foregroundColor, background: p.backgroundColor };
    });

    return {
      type: 'schema',
      valuesMap: colors,
    };
  }

  private createColorsFromSinglePalette(
    tagsPalette?: PropertiesConfigInputColorPaletteModel,
  ): TagColorPalette | undefined {
    const { foreground, background } = tagsPalette || {};
    if (!foreground || !background) {
      return;
    }

    const colors = this.themeService.process({
      foreground,
      background,
    }) as TagColorInfo;

    colors.background = this.normalizeSinglePaletteBgColor(
      colors.background,
      this.themeService.activeTheme?.value,
    );

    return {
      type: 'single',
      single: colors,
    };
  }

  private createColorsFromPalette(
    tagsPalette?: PropertiesConfigInputColorPaletteModel,
    values: any[] = [],
    overrideForeground?: string,
    triplePalette?: boolean,
  ): TagColorPalette | undefined {
    if (!tagsPalette) {
      return;
    }

    const possibleValues = new Set<string>();
    values.forEach((value) => {
      if (isNullish(value)) {
        return;
      }

      value = getDataValue(value);

      if (Array.isArray(value)) {
        value.forEach((tag) => {
          possibleValues.add(tag);
        });
      } else {
        possibleValues.add(value);
      }
    });

    const backgroundColors: string[] = [];
    const colors: Record<string, TagColorInfo> = {};

    for (let i = 0; i < COLOR_PALETTE_LENGTH; i += 1) {
      backgroundColors.push(makeThemePaletteValueCss(tagsPalette.paletteName, 'background', i));
    }
    if (triplePalette) {
      for (let i = 0; i < COLOR_PALETTE_LENGTH; i += 1) {
        backgroundColors.push(
          getCssMixedColor(
            makeThemePaletteValueCss(tagsPalette.paletteName, 'background', i),
            '#000',
            10,
          ),
        );
      }
      for (let i = 0; i < COLOR_PALETTE_LENGTH; i += 1) {
        backgroundColors.push(
          getCssMixedColor(
            makeThemePaletteValueCss(tagsPalette.paletteName, 'background', i),
            '#fff',
            24,
          ),
        );
      }
    }

    let index = 0;
    possibleValues.forEach((option) => {
      const colorIndex = index % backgroundColors.length;
      colors[option] = {
        foreground:
          overrideForeground ?? makeThemePaletteValueCss(tagsPalette.paletteName, 'foreground'),
        background: backgroundColors[colorIndex],
      };
      index += 1;
    });

    return {
      type: tagsPalette?.paletteName || '',
      valuesMap: colors,
    };
  }
}
